import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  CalendarIcon,
  Callout,
  ChartTrendingUpIcon,
  ChevronDownIcon,
  DownloadIcon,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  RefreshIcon,
  Switch,
  TableIcon,
  TrashIcon,
  useConfirm,
} from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import {
  deleteEFormReportSchedule,
  downloadEFormReportScheduleArtifact,
  drilldownEFormReport,
  queryEFormReport,
  runEFormReportSchedule,
  saveEFormReportSchedule,
} from '../../services/mutations';
import ReportPivotResult from './ReportPivotResult';
import { pivotReport } from './reportPivot';
import {
  BUCKET_OPTIONS,
  FILTER_OP_LABELS,
  createCatalogLookups,
  dimLabel,
  filterLabel,
  fromQuery,
  measureFnLabel,
  measureLabel,
  toQuery,
  type ReportPivotDraft,
} from './reportPivotModel';
import {
  type EFormReportCatalog,
  type EFormReportQueryResult,
  type EFormReportSchedule,
  type EFormSavedReport,
  useEFormDefinition,
  useEFormReportCatalog,
  useEFormReportSchedules,
  useEFormSavedReports,
} from './useData';

/**
 * Read-only viewer for a saved pivot report.
 * Route: `/e-forms/:definitionId/reports/:reportId`
 * Mirrors submissions as the primary consumer surface; sidebar can deep-link here later.
 */
const SavedReportViewPage: React.FC = () => {
  const { definitionId: defParam, reportId: reportParam } = useParams<{
    definitionId: string;
    reportId: string;
  }>();
  const definitionId = defParam ? parseInt(defParam, 10) : NaN;
  const reportId = reportParam ? parseInt(reportParam, 10) : NaN;
  const { colors } = useTheme();
  const { showError } = useNotification();
  const { setTitleOverride } = useTopbar();
  const navigate = useNavigate();

  const def = useEFormDefinition(Number.isInteger(definitionId) ? definitionId : 0);
  const catalog = useEFormReportCatalog(Number.isInteger(definitionId) ? definitionId : null);
  const saved = useEFormSavedReports(Number.isInteger(definitionId) ? definitionId : null);

  const [result, setResult] = useState<EFormReportQueryResult | null>(null);
  const [running, setRunning] = useState(false);

  const report: EFormSavedReport | undefined = useMemo(
    () => (saved.data || []).find((r) => r.id === reportId),
    [saved.data, reportId],
  );

  const draft = useMemo(() => {
    if (!report) return null;
    return fromQuery(report.queryJson || {}, report.chartType);
  }, [report]);

  const queryPayload = useMemo(() => {
    if (!draft || !report) return null;
    const q = report.queryJson || {};
    return toQuery(draft, { approvedOnly: Boolean(q.approvedOnly) });
  }, [draft, report]);

  const formName = def.data?.name ?? catalog.data?.name ?? 'E-Form';

  useEffect(() => {
    if (report?.name) setTitleOverride(`${report.name} · ${formName}`);
    else setTitleOverride(formName);
    return () => setTitleOverride(undefined);
  }, [report?.name, formName, setTitleOverride]);

  const runQuery = useCallback(async () => {
    if (!Number.isInteger(definitionId) || !queryPayload) return;
    setRunning(true);
    try {
      const data = await queryEFormReport(definitionId, queryPayload);
      setResult(data);
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Report query failed');
    } finally {
      setRunning(false);
    }
  }, [definitionId, queryPayload, showError]);

  useEffect(() => {
    if (!catalog.data || !queryPayload) return;
    void runQuery();
  }, [catalog.data, queryPayload, runQuery]);

  const display = useMemo(
    () => (result && draft ? pivotReport(result, { showTotals: draft.showTotals }) : null),
    [result, draft],
  );

  const lookups = useMemo(
    () => (catalog.data ? createCatalogLookups(catalog.data) : null),
    [catalog.data],
  );

  const handleDrilldown = async (
    row: Record<string, unknown>,
    colEquals?: Record<string, unknown>,
  ) => {
    if (!queryPayload || row.__total) return;
    try {
      const dimEquals: Record<string, unknown> = { ...(colEquals || {}) };
      (result?.columns || [])
        .filter((c) => c.kind === 'dimension' && c.axis !== 'column')
        .forEach((c) => {
          dimEquals[c.key] = row[c.key];
        });
      const data = await drilldownEFormReport(definitionId, {
        ...queryPayload,
        dimensionEquals: dimEquals,
      });
      if (!data.ids.length) {
        showError('No matching submissions');
        return;
      }
      navigate(`/e-forms/${definitionId}/submissions?ids=${data.ids.join(',')}`);
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Drill-down failed');
    }
  };

  const handleExport = async () => {
    if (!display?.rows.length) return;
    const { exportToExcel } = await import('../../components/exports');
    await exportToExcel({
      sortedData: display.rows,
      tableColumns: display.columns.map((c) => ({
        key: c.key,
        header: c.group ? `${c.group} · ${c.label}` : c.label,
      })),
      entityName: report?.name ?? formName,
      exportFileName: `${def.data?.code ?? 'eform'}-${report?.name ?? 'report'}`,
    });
  };

  if (!Number.isInteger(definitionId) || !Number.isInteger(reportId)) {
    return <div className="p-6">Invalid report</div>;
  }

  if (catalog.isLoading || def.isLoading || saved.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="large" message="Loading report…" />
      </div>
    );
  }

  if (catalog.isError || !catalog.data) {
    return (
      <div className={cn('p-6', colors.text)}>
        <Callout variant="danger" title="Could not load report">
          <Link to={`/e-forms/${definitionId}/submissions`}>Back to submissions</Link>
        </Callout>
      </div>
    );
  }

  if (!saved.isLoading && !report) {
    return (
      <div className={cn('p-6', colors.text)}>
        <Callout variant="danger" title="Report not found">
          <Link to={`/e-forms/${definitionId}/reports`}>Open report builder</Link>
        </Callout>
      </div>
    );
  }

  if (!draft || !lookups) return null;

  const cat = catalog.data as EFormReportCatalog;

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-3', colors.text)}>
      <div className="flex shrink-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
                colors.border,
                'bg-slate-50 dark:bg-slate-800/60',
              )}
            >
              <ChartTrendingUpIcon className={cn('h-5 w-5', colors.textMuted)} />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold tracking-tight">{report!.name}</h1>
                <Badge variant="secondary">Report</Badge>
              </div>
              <p className={cn('text-sm', colors.textMuted)}>
                <span className="font-medium text-slate-600 dark:text-slate-300">{cat.name}</span>
                {report!.description ? (
                  <span className="before:mx-1.5 before:content-['·']">{report!.description}</span>
                ) : null}
              </p>
            </div>
          </div>
          <PivotAxisSummary draft={draft} lookups={lookups} />
        </div>

        <div className="flex h-10 shrink-0 flex-wrap items-center gap-2">
          <Link
            to={`/e-forms/${definitionId}/submissions`}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium',
              colors.border,
              colors.text,
              colors.interactiveHover,
            )}
          >
            <TableIcon className="h-4 w-4" />
            Submissions
          </Link>
          <button
            type="button"
            disabled={!display?.rows.length}
            onClick={() => void handleExport()}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium disabled:opacity-50',
              colors.border,
              colors.text,
              colors.interactiveHover,
            )}
          >
            <DownloadIcon className="h-4 w-4" />
            Export Excel
          </button>
          <button
            type="button"
            disabled={running}
            onClick={() => void runQuery()}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-white shadow-sm disabled:opacity-60',
              colors.primary,
            )}
          >
            <RefreshIcon className={cn('h-4 w-4', running && 'animate-spin')} />
            {running ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <ReportSchedulePanel reportId={reportId} />

      <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <ReportPivotResult
          result={result}
          chartType={draft.chartType}
          showTotals={draft.showTotals}
          running={running}
          emptyHint="This saved report returned no rows."
          onCellClick={(row, colEquals) => void handleDrilldown(row, colEquals)}
        />
      </div>
    </div>
  );
};

function PivotAxisSummary({
  draft,
  lookups,
}: {
  draft: ReportPivotDraft;
  lookups: ReturnType<typeof createCatalogLookups>;
}) {
  const chips: Array<{ key: string; label: string; tone: string }> = [];

  for (const d of draft.rows) {
    const bucket = d.bucket
      ? BUCKET_OPTIONS.find((o) => o.value === d.bucket)?.label || d.bucket
      : null;
    chips.push({
      key: `r-${d.uid}`,
      label: `Rows · ${dimLabel(d, lookups)}${bucket ? ` (${bucket})` : ''}`,
      tone: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    });
  }
  for (const d of draft.columns) {
    const bucket = d.bucket
      ? BUCKET_OPTIONS.find((o) => o.value === d.bucket)?.label || d.bucket
      : null;
    chips.push({
      key: `c-${d.uid}`,
      label: `Columns · ${dimLabel(d, lookups)}${bucket ? ` (${bucket})` : ''}`,
      tone: 'bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200',
    });
  }
  for (const m of draft.values) {
    const field = lookups.findField(m.fieldId, m.relationId);
    chips.push({
      key: `v-${m.uid}`,
      label: `Values · ${measureLabel(m, lookups)} (${measureFnLabel(m.fn, field?.dataType, m.metaKey)})`,
      tone: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
    });
  }
  for (const f of draft.filters) {
    const op = FILTER_OP_LABELS[f.op] || f.op;
    chips.push({
      key: `f-${f.uid}`,
      label: `Filter · ${filterLabel(f, lookups)} ${op}`,
      tone: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
    });
  }

  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5 pl-[3.25rem]">
      {chips.map((c) => (
        <span
          key={c.key}
          className={cn(
            'inline-flex max-w-full items-center truncate rounded-full px-2.5 py-1 text-xs font-medium',
            c.tone,
          )}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

type CronPreset = 'daily' | 'weekly' | 'monthly' | 'custom';

type ScheduleDraft = {
  name: string;
  cron: string;
  timezone: string;
  recipients: string;
  format: 'EXCEL' | 'PDF';
  isEnabled: boolean;
};

const CRON_PRESETS: Record<Exclude<CronPreset, 'custom'>, string> = {
  daily: '0 8 * * *',
  weekly: '0 8 * * 1',
  monthly: '0 8 1 * *',
};

function blankScheduleDraft(): ScheduleDraft {
  return {
    name: '',
    cron: CRON_PRESETS.daily,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    recipients: '',
    format: 'EXCEL',
    isEnabled: true,
  };
}

function scheduleToDraft(schedule: EFormReportSchedule): ScheduleDraft {
  return {
    name: schedule.name,
    cron: schedule.cron,
    timezone: schedule.timezone || 'UTC',
    recipients: schedule.recipients.join(', '),
    format: schedule.format,
    isEnabled: schedule.isEnabled,
  };
}

function cronPreset(cron: string): CronPreset {
  const match = Object.entries(CRON_PRESETS).find(([, value]) => value === cron);
  return (match?.[0] as CronPreset | undefined) ?? 'custom';
}

function scheduleErrorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  return (Array.isArray(message) ? message.join(', ') : message) || error?.message || fallback;
}

function formatRunDate(value?: string | null) {
  if (!value) return 'Never run';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function ReportSchedulePanel({ reportId }: { reportId: number }) {
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const schedules = useEFormReportSchedules(reportId);
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<ScheduleDraft>(() => blankScheduleDraft());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const startCreate = () => {
    setForm(blankScheduleDraft());
    setEditingId('new');
    setIsOpen(true);
  };

  const startEdit = (schedule: EFormReportSchedule) => {
    setForm(scheduleToDraft(schedule));
    setEditingId(schedule.id);
    setIsOpen(true);
  };

  const saveSchedule = async (event: React.FormEvent) => {
    event.preventDefault();
    const recipients = form.recipients
      .split(',')
      .map((recipient) => recipient.trim())
      .filter(Boolean);
    if (!form.name.trim() || !form.cron.trim() || !recipients.length) {
      showError('Name, cron, and at least one recipient are required');
      return;
    }
    setSaving(true);
    try {
      await saveEFormReportSchedule(
        reportId,
        {
          name: form.name.trim(),
          cron: form.cron.trim(),
          timezone: form.timezone.trim() || 'UTC',
          recipients,
          format: form.format,
          isEnabled: form.isEnabled,
        },
        typeof editingId === 'number' ? editingId : undefined,
      );
      await schedules.mutate();
      showSuccess(editingId === 'new' ? 'Schedule created' : 'Schedule updated');
      setEditingId(null);
    } catch (error: any) {
      showError(scheduleErrorMessage(error, 'Could not save schedule'));
    } finally {
      setSaving(false);
    }
  };

  const removeSchedule = async (schedule: EFormReportSchedule) => {
    const ok = await confirm({
      title: 'Delete schedule',
      message: `Delete “${schedule.name}”? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setBusyId(schedule.id);
    try {
      await deleteEFormReportSchedule(reportId, schedule.id);
      await schedules.mutate();
      if (editingId === schedule.id) setEditingId(null);
      showSuccess('Schedule deleted');
    } catch (error: any) {
      showError(scheduleErrorMessage(error, 'Could not delete schedule'));
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (schedule: EFormReportSchedule) => {
    setBusyId(schedule.id);
    try {
      await runEFormReportSchedule(reportId, schedule.id);
      await schedules.mutate();
      showSuccess(`“${schedule.name}” ran successfully`);
    } catch (error: any) {
      await schedules.mutate();
      showError(scheduleErrorMessage(error, 'Schedule run failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex min-h-11 items-center justify-between gap-3 px-3 py-2">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <CalendarIcon className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold">Schedule</span>
          <Badge variant="secondary">{schedules.data.length}</Badge>
          <ChevronDownIcon
            className={cn('h-4 w-4 text-slate-400 transition-transform', isOpen && 'rotate-180')}
          />
        </button>
        <button
          type="button"
          onClick={startCreate}
          className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          New
        </button>
      </div>

      {isOpen ? (
        <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-700">
          {schedules.isError ? (
            <p className="text-sm text-red-600 dark:text-red-400">Could not load schedules.</p>
          ) : schedules.isLoading ? (
            <div className="py-2">
              <LoadingSpinner size="small" message="Loading schedules…" />
            </div>
          ) : (
            <div className="space-y-2">
              {!schedules.data.length && editingId !== 'new' ? (
                <p className="py-1 text-sm text-slate-500">No schedules yet.</p>
              ) : null}
              {schedules.data.map((schedule) => (
                <div
                  key={schedule.id}
                  className="flex flex-col gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{schedule.name}</span>
                      <Badge variant={schedule.isEnabled ? 'success' : 'secondary'}>
                        {schedule.isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {cronPreset(schedule.cron) === 'custom'
                          ? schedule.cron
                          : cronPreset(schedule.cron)}
                        {' · '}
                        {schedule.format}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">
                      {schedule.recipients.join(', ')} · {formatRunDate(schedule.lastRunAt)}
                    </div>
                    {schedule.lastError ? (
                      <div
                        className="mt-1 truncate text-xs text-red-600 dark:text-red-400"
                        title={schedule.lastError}
                      >
                        Last error: {schedule.lastError}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {schedule.lastFileStorageKey ? (
                      <button
                        type="button"
                        aria-label={`Download latest ${schedule.name}`}
                        disabled={busyId !== null}
                        onClick={() =>
                          void downloadEFormReportScheduleArtifact(reportId, schedule.id)
                        }
                        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-800"
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void runNow(schedule)}
                      className="h-8 rounded-lg border border-slate-200 px-2.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
                    >
                      {busyId === schedule.id ? 'Running…' : 'Run now'}
                    </button>
                    <button
                      type="button"
                      aria-label={`Edit ${schedule.name}`}
                      disabled={busyId !== null}
                      onClick={() => startEdit(schedule)}
                      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-800"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${schedule.name}`}
                      disabled={busyId !== null}
                      onClick={() => void removeSchedule(schedule)}
                      className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/30"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {editingId !== null ? (
            <ScheduleEditor
              form={form}
              saving={saving}
              isNew={editingId === 'new'}
              onChange={setForm}
              onCancel={() => setEditingId(null)}
              onSubmit={saveSchedule}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ScheduleEditor({
  form,
  saving,
  isNew,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: ScheduleDraft;
  saving: boolean;
  isNew: boolean;
  onChange: React.Dispatch<React.SetStateAction<ScheduleDraft>>;
  onCancel: () => void;
  onSubmit: (event: React.FormEvent) => void;
}) {
  const preset = cronPreset(form.cron);
  const inputClass =
    'h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950';

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3 dark:border-blue-900 dark:bg-blue-950/20"
    >
      <div className="mb-2 text-sm font-semibold">{isNew ? 'New schedule' : 'Edit schedule'}</div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Name
          <input
            autoFocus
            maxLength={160}
            value={form.name}
            onChange={(event) => onChange((value) => ({ ...value, name: event.target.value }))}
            className={cn(inputClass, 'mt-1')}
            placeholder="Monthly report"
          />
        </label>
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Frequency
          <select
            value={preset}
            onChange={(event) => {
              const value = event.target.value as CronPreset;
              if (value !== 'custom') {
                onChange((current) => ({ ...current, cron: CRON_PRESETS[value] }));
              } else if (preset !== 'custom') {
                onChange((current) => ({ ...current, cron: '' }));
              }
            }}
            className={cn(inputClass, 'mt-1')}
          >
            <option value="daily">Daily at 08:00</option>
            <option value="weekly">Weekly, Monday at 08:00</option>
            <option value="monthly">Monthly, day 1 at 08:00</option>
            <option value="custom">Custom cron</option>
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Recipients
          <input
            value={form.recipients}
            onChange={(event) =>
              onChange((value) => ({ ...value, recipients: event.target.value }))
            }
            className={cn(inputClass, 'mt-1')}
            placeholder="ops@example.com, master@example.com"
          />
        </label>
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Format
          <select
            value={form.format}
            onChange={(event) =>
              onChange((value) => ({
                ...value,
                format: event.target.value as ScheduleDraft['format'],
              }))
            }
            className={cn(inputClass, 'mt-1')}
          >
            <option value="EXCEL">Excel</option>
            <option value="PDF">PDF</option>
          </select>
        </label>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {preset === 'custom' ? (
          <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
            Cron (5 fields)
            <input
              value={form.cron}
              onChange={(event) => onChange((value) => ({ ...value, cron: event.target.value }))}
              className={cn(inputClass, 'mt-1 font-mono')}
              placeholder="0 8 * * *"
            />
          </label>
        ) : null}
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Timezone
          <input
            maxLength={80}
            value={form.timezone}
            onChange={(event) => onChange((value) => ({ ...value, timezone: event.target.value }))}
            className={cn(inputClass, 'mt-1')}
            placeholder="UTC"
          />
        </label>
        <div className="flex items-end pb-2">
          <Switch
            checked={form.isEnabled}
            onChange={(event) =>
              onChange((value) => ({ ...value, isEnabled: event.target.checked }))
            }
            label="Enabled"
            size="sm"
          />
        </div>
        <div className="flex items-end justify-end gap-2 sm:col-span-2 lg:col-span-1">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-medium hover:bg-white disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-9 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default SavedReportViewPage;
